import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Resource } from "react-admin";
import { useTokenOps } from "../components/TokenContext";
import { hostname } from "../providers/api";
import { CarbonAdminPage, CarbonDataTable, CarbonSection, CarbonSelectInput, CarbonTextInput, CarbonFilterBar, CarbonTableRow } from "./corporateCarbon/CarbonAdminPage";
import { ReportingRevisionFilter } from './corporateCarbon/ReportingRevisionFilter';


import { carbonResourcePath } from "./corporateCarbon/workflow";

const PAGE_SIZE = 25;

const fetchAudit = async (token, page, filters = {}) => {
  const query = new URLSearchParams(Object.entries(filters).filter(([, value]) => value));
  const response = await fetch(
    `${hostname}/corporate-carbon/audit?page=${page}&perPage=${PAGE_SIZE}${query.toString() ? `&${query}` : ''}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const body = await response.json();
  if (!response.ok)
    throw new Error(body.message || "Audit history is unavailable.");
  if (!body || !Array.isArray(body.data) || !Number.isInteger(body.total))
    throw new Error("Invalid audit history response.");
  return body;
};

const Snapshot = ({ label, value }) => (
  <div>
    <Typography variant="subtitle2">{label}</Typography>
    <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
      {JSON.stringify(value, null, 2)}
    </pre>
  </div>
);

export const CarbonAuditTimeline = ({ accessToken, history }) => {
  const tokenOps = useTokenOps();
  const token = accessToken || tokenOps.account?.accessToken;
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const filter = (key, value) => { setPage(1); setFilters(current => ({ ...current, [key]: value })); };
  const [result, setResult] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    if (!token) {
      setError(
        new Error("An active account token is required to load audit history.")
      );
      setLoading(false);
      return () => {
        active = false;
      };
    }
    fetchAudit(token, page, filters)
      .then((next) => {
        if (active) setResult(next);
      })
      .catch((nextError) => {
        if (active) setError(nextError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, token, filters]);

  useEffect(() => { setPage(1); setFilters(current => Object.keys(current).length ? {} : current); setResult({ data: [], total: 0 }); }, [token]);

  return (
    <CarbonAdminPage
      title="Carbon audit history"
      description="Review the immutable account activity trail and inspect recorded changes."
      variant="list"
    >
        <CarbonFilterBar label="Audit filters">
          <CarbonSelectInput label="Action" name="action" value={filters.action || ''} emptyText="All actions" choices={['created','updated','archived','finalized','superseded','linked','replaced','download_opened','recalculated','exported','imported'].map(id => ({ id, name: id.replace(/_/g, ' ') }))} onChange={event => filter('action', event.target.value)} />
          <CarbonSelectInput label="Record type" name="entityType" value={filters.entityType || ''} emptyText="All types" choices={['CarbonProfile','CarbonSite','CarbonInventory','CarbonInventoryRevision','InventoryBoundarySite','CarbonActivity','CarbonEvidenceDocument','CarbonCalculationRun','CarbonExportRecord','CarbonImportBatch'].map(id => ({ id, name: id.replace(/^Carbon/, '').replace(/([a-z])([A-Z])/g, '$1 $2') }))} onChange={event => filter('entityType', event.target.value)} />
          <CarbonTextInput label="Actor name or email" name="actor" value={filters.actor || ''} onChange={event => filter('actor', event.target.value)} />
          <CarbonTextInput label="From" name="from" type="date" value={filters.from || ''} onChange={event => filter('from', event.target.value)} />
          <CarbonTextInput label="To" name="to" type="date" value={filters.to || ''} onChange={event => filter('to', event.target.value)} />
          <ReportingRevisionFilter token={token} value={filters.revisionId || ''} onChange={value => filter('revisionId', value)} inventoryId={filters.inventoryId || ''} onInventoryChange={value => { setPage(1); setFilters(current => ({ ...current, inventoryId: value, revisionId: '' })); }} />
        </CarbonFilterBar>
        {loading && (
          <div role="status">
            <CircularProgress size={20} /> Loading audit history...
          </div>
        )}
        {!loading && error && (
          <Typography role="alert" color="error">
            {error.message}
          </Typography>
        )}
        {!loading && !error && result.data.length === 0 && (
          <Typography>No audit events found.</Typography>
        )}
        {!loading && !error && result.data.length > 0 && (
          <CarbonSection title="Audit events" description="Immutable changes recorded for the active account. Open an event to inspect its before-and-after snapshots.">
            <CarbonDataTable label="Carbon audit events">
              <TableHead><TableRow><TableCell>Action</TableCell><TableCell>Record</TableCell><TableCell>Actor</TableCell><TableCell>Date</TableCell><TableCell>Reason</TableCell></TableRow></TableHead>
              <TableBody>{result.data.map((entry) => (
                <CarbonTableRow ariaLabel={`View audit event ${entry.action}`} key={entry.id} onSelect={() => history.push(carbonResourcePath("carbonAudit", entry.id))}>
                  <TableCell>{entry.action}</TableCell>
                  <TableCell>{entry.recordLabel || entry.entityType.replace(/^Carbon/, '').replace(/([a-z])([A-Z])/g, '$1 $2')}</TableCell>
                  <TableCell>{entry.actorLabel || 'User not recorded'}</TableCell>
                  <TableCell><time dateTime={entry.occurredAt}>{new Date(entry.occurredAt).toLocaleString()}</time></TableCell>
                  <TableCell>
                    {entry.reason && <Typography variant="body2">{entry.reason}</Typography>}
                  </TableCell>
                </CarbonTableRow>
              ))}</TableBody>
            </CarbonDataTable>
          </CarbonSection>
        )}
        {!loading && !error && (
          <nav aria-label="Audit history pagination" style={{ marginTop: 16 }}>
            <Button
              aria-label="Previous page"
              disabled={page === 1}
              onClick={() => setPage((value) => value - 1)}
            >
              Previous
            </Button>
            <Button
              aria-label="Next page"
              disabled={page * PAGE_SIZE >= result.total}
              onClick={() => setPage((value) => value + 1)}
            >
              Next
            </Button>
          </nav>
        )}
    </CarbonAdminPage>
  );
};

export const CarbonAuditShow = ({ accessToken, id }) => {
  const tokenOps = useTokenOps();
  const token = accessToken || tokenOps.account?.accessToken;
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    const findEntry = async () => {
      let page = 1;
      while (active) {
        const result = await fetchAudit(token, page);
        const match = result.data.find((item) => item.id === id);
        if (match) return match;
        if (page * PAGE_SIZE >= result.total) return null;
        page += 1;
      }
      return null;
    };
    setLoading(true);
    setEntry(null);
    setError("");
    findEntry().then((value) => {
      if (active) setEntry(value);
    }).catch((requestError) => {
      if (active) setError(requestError.message);
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [id, token]);
  return (
    <CarbonAdminPage title="Audit event" description="Immutable before-and-after record for this account change.">
      {loading ? <div role="status">Loading audit event…</div> : null}
      {error ? <Typography color="error" role="alert">{error}</Typography> : null}
      {!loading && !error && !entry ? <Typography role="alert">Audit event not found.</Typography> : null}
      {!loading && !error && entry ? <CarbonSection title={`${entry.action} · ${entry.entityType.replace(/^Carbon/, '').replace(/([a-z])([A-Z])/g, '$1 $2')}`}>
        <dl><dt>Record</dt><dd>{entry.recordLabel || "Record not recorded"}</dd><dt>Actor</dt><dd>{entry.actorLabel || "System / batch import"}</dd><dt>Date</dt><dd><time dateTime={entry.occurredAt}>{new Date(entry.occurredAt).toLocaleString()}</time></dd><dt>Reason</dt><dd>{entry.reason || "No reason recorded"}</dd></dl>
        <Snapshot label="Before" value={entry.before} />
        <Snapshot label="After" value={entry.after} />
      </CarbonSection> : null}
    </CarbonAdminPage>
  );
};

const carbonAuditResource = () => (
  <Resource
    name="carbonAudit"
    options={{ label: "Audit history", menuGroup: "Carbon Accounting" }}
    list={CarbonAuditTimeline}
    show={CarbonAuditShow}
  />
);

export default carbonAuditResource;
